
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  GraduationCap, 
  Plus, 
  Users, 
  Calendar, 
  DollarSign, 
  Star, 
  BarChart,
  Clock,
  Edit,
  Trash,
  Eye
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrainingModal, TrainingProgramData } from "@/components/training/TrainingModal";
import { motion } from "framer-motion";

// Sample data for training programs
const sampleTrainingPrograms: TrainingProgramData[] = [
  {
    title: "Frontend Development Masterclass",
    description: "A comprehensive course covering modern frontend frameworks and best practices.",
    category: "technical",
    format: "course",
    price: "399",
    duration: "6 weeks",
    level: "intermediate",
    maxParticipants: "15",
    isRemote: true,
    isCertified: true
  },
  {
    title: "UI/UX Design Principles",
    description: "Learn the fundamentals of user interface and experience design.",
    category: "design",
    format: "workshop",
    price: "249",
    duration: "4 weeks",
    level: "beginner",
    maxParticipants: "20",
    isRemote: true,
    isCertified: false
  }
];

const FreelancerTraining = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("active");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [trainingPrograms, setTrainingPrograms] = useState(sampleTrainingPrograms);

  const handleCreateTraining = (data: TrainingProgramData) => {
    setTrainingPrograms(prev => [...prev, data]);
  };

  const handleDeleteTraining = (index: number) => {
    toast({
      title: "Training program deleted",
      description: "Your training program has been removed.",
    });
    setTrainingPrograms(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditTraining = (index: number) => {
    toast({
      title: "Edit feature",
      description: "Edit functionality will be implemented in the next phase.",
    });
  };

  const handleViewTraining = (index: number) => {
    toast({
      title: "View details",
      description: "Detailed view will be implemented in the next phase.",
    });
  };

  const getFormatIcon = (format: string) => {
    switch(format) {
      case 'workshop': return <Users className="h-4 w-4" />;
      case 'webinar': return <Users className="h-4 w-4" />;
      case 'bootcamp': return <Calendar className="h-4 w-4" />;
      case 'course': return <GraduationCap className="h-4 w-4" />;
      case 'mentoring': return <Star className="h-4 w-4" />;
      default: return <GraduationCap className="h-4 w-4" />;
    }
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-primary" />
              My Training Programs
            </h1>
            <p className="text-muted-foreground">
              Create and manage your training offerings for clients and organizations
            </p>
          </div>
          <Button 
            onClick={() => setIsModalOpen(true)} 
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Create New Program
          </Button>
        </div>

        <div className="bg-muted/30 rounded-lg p-4 mb-8 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Programs</p>
              <p className="text-2xl font-bold">{trainingPrograms.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Enrollments</p>
              <p className="text-2xl font-bold">23</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold">$4,320</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="flex items-center gap-2 whitespace-nowrap">
            <BarChart className="h-4 w-4" />
            View Analytics
          </Button>
        </div>
        
        <Tabs defaultValue="active" value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="draft">Drafts</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="grid gap-6"
            >
              {trainingPrograms.length > 0 ? (
                trainingPrograms.map((program, index) => (
                  <motion.div key={index} variants={item}>
                    <Card className="relative overflow-hidden">
                      <div className={`absolute top-0 left-0 w-1 h-full ${
                        program.category === 'technical' ? 'bg-blue-500' :
                        program.category === 'design' ? 'bg-purple-500' :
                        program.category === 'business' ? 'bg-amber-500' :
                        program.category === 'marketing' ? 'bg-emerald-500' :
                        'bg-gray-500'
                      }`} />
                      <CardHeader className="pb-2">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                          <div>
                            <CardTitle className="text-xl">{program.title}</CardTitle>
                            <CardDescription className="line-clamp-2 mt-1">
                              {program.description}
                            </CardDescription>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              ${program.price}
                            </Badge>
                            <Badge variant="outline" className="flex items-center gap-1">
                              {getFormatIcon(program.format)}
                              {program.format.charAt(0).toUpperCase() + program.format.slice(1)}
                            </Badge>
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {program.duration}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="secondary">
                            {program.level.charAt(0).toUpperCase() + program.level.slice(1)}
                          </Badge>
                          {program.isRemote && (
                            <Badge variant="secondary">Remote</Badge>
                          )}
                          {program.isCertified && (
                            <Badge variant="secondary">Certificate</Badge>
                          )}
                          {program.maxParticipants && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              Max {program.maxParticipants}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-between pt-2">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">0</span> current enrollments
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewTraining(index)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTraining(index)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTraining(index)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No active training programs</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first training program to share your expertise with clients
                  </p>
                  <Button 
                    onClick={() => setIsModalOpen(true)} 
                    className="flex items-center gap-2 mx-auto"
                  >
                    <Plus className="h-4 w-4" />
                    Create New Program
                  </Button>
                </div>
              )}
            </motion.div>
          </TabsContent>
          
          <TabsContent value="draft">
            <div className="text-center py-12 bg-muted/30 rounded-lg">
              <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No draft programs</h3>
              <p className="text-muted-foreground mb-4">
                Save your training programs as drafts to edit them later
              </p>
            </div>
          </TabsContent>
          
          <TabsContent value="archived">
            <div className="text-center py-12 bg-muted/30 rounded-lg">
              <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No archived programs</h3>
              <p className="text-muted-foreground mb-4">
                Archived programs are not visible to clients but can be restored
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TrainingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onCreate={handleCreateTraining}
      />
    </div>
  );
};

export default FreelancerTraining;
